<!-- src/pages/OurSocialsPage.vue -->
<template>
    <main class="socials-page" aria-label="Our socials page">
        <!-- HERO -->
        <section class="socials-hero" aria-labelledby="socials-title">
            <div class="socials-hero__glow socials-hero__glow--one" aria-hidden="true"></div>

            <div class="socials-hero__glow socials-hero__glow--two" aria-hidden="true"></div>

            <div class="socials-page__shell socials-hero__shell">
                <div class="socials-hero__content">
                    <p class="socials-eyebrow">
                        M.C Bridge of Hope
                    </p>

                    <h1 id="socials-title" class="socials-hero__title">
                        Follow our journey online.
                    </h1>

                    <p class="socials-hero__text">
                        Stay connected through community moments,
                        outreach updates, impact stories, events,
                        and opportunities to support the mission.
                    </p>

                    <div class="socials-hero__actions">
                        <a class="socials-btn socials-btn--primary" :href="featuredSocial.url" target="_blank"
                            rel="noopener noreferrer">
                            Follow us
                        </a>

                        <RouterLink class="socials-btn socials-btn--light" to="/contact">
                            Contact us
                        </RouterLink>
                    </div>
                </div>

                <!-- FEATURED SOCIAL -->
                <aside class="feature-card" aria-label="Featured social platform">
                    <div class="feature-card__imageWrap">
                        <img class="feature-card__image" :src="featuredSocial.image" :alt="featuredSocial.imageAlt" />

                        <div class="feature-card__badge">
                            {{ featuredSocial.label }}
                        </div>
                    </div>

                    <div class="feature-card__body">
                        <div class="feature-card__icon" v-html="featuredSocial.icon" aria-hidden="true"></div>

                        <div>
                            <p class="feature-card__small">
                                Featured platform
                            </p>

                            <h2>
                                {{ featuredSocial.name }}
                            </h2>

                            <p>
                                {{ featuredSocial.handle }}
                            </p>
                        </div>
                    </div>
                </aside>
            </div>
        </section>

        <!-- SOCIAL GRID -->
        <section class="socials-section" aria-label="Social media platforms">
            <div class="socials-page__shell">
                <header class="socials-section__head">
                    <p class="socials-eyebrow">
                        Connect with us
                    </p>

                    <h2>
                        See our community online
                    </h2>

                    <p>
                        Explore the platforms where we share updates,
                        stories, outreach highlights, community moments,
                        and ways to get involved.
                    </p>
                </header>

                <div class="socials-grid">
                    <a v-for="item in socialsList" :key="item.name" class="social-card" :href="item.url" target="_blank"
                        rel="noopener noreferrer" :aria-label="`Open ${item.name}`">
                        <div class="social-card__media">
                            <img class="social-card__image" :src="item.image" :alt="item.imageAlt" loading="lazy" />

                            <div class="social-card__overlay">
                                <span>
                                    {{ item.label }}
                                </span>

                                <strong class="social-card__platform-icon" v-html="item.icon"
                                    aria-hidden="true"></strong>
                            </div>
                        </div>

                        <div class="social-card__body">
                            <div>
                                <h3>
                                    {{ item.name }}
                                </h3>

                                <p class="social-card__handle">
                                    {{ item.handle }}
                                </p>
                            </div>

                            <p class="social-card__desc">
                                {{ item.description }}
                            </p>

                            <span class="social-card__link">
                                Open platform
                            </span>
                        </div>
                    </a>
                </div>
            </div>
        </section>

        <!-- PHOTO STRIP -->
        <section class="gallery-section" aria-label="Community gallery">
            <div class="socials-page__shell">
                <header class="gallery-section__head">
                    <p class="socials-eyebrow">
                        Stories worth sharing
                    </p>

                    <h2>
                        Community, care, and hope in action
                    </h2>

                    <p>
                        Every photo reflects the people, service,
                        and shared purpose behind M.C Bridge of Hope.
                    </p>
                </header>

                <div class="gallery-grid">
                    <article v-for="(photo, index) in galleryPhotos" :key="index" class="gallery-card"
                        :class="`gallery-card--${index + 1}`">
                        <img class="gallery-card__image" :src="photo.src" :alt="photo.alt" loading="lazy" />

                        <div class="gallery-card__shade" aria-hidden="true"></div>
                    </article>
                </div>
            </div>
        </section>

        <!-- CTA -->
        <section class="socials-cta" aria-label="Get involved">
            <div class="socials-page__shell">
                <div class="socials-cta__panel">
                    <div class="socials-cta__content">
                        <p class="socials-eyebrow">
                            Help spread hope
                        </p>

                        <h2>
                            Follow, share, and support the mission.
                        </h2>

                        <p>
                            Every follow, share, message, and donation
                            helps more people discover the work, stories,
                            and support behind M.C Bridge of Hope.
                        </p>

                        <div class="socials-cta__actions">
                            <a class="socials-btn socials-btn--primary" :href="featuredSocial.url" target="_blank"
                                rel="noopener noreferrer">
                                Follow us
                            </a>

                            <RouterLink class="socials-btn socials-btn--light" to="/donate">
                                Donate
                            </RouterLink>
                        </div>
                    </div>

                    <div class="cta-collage" aria-hidden="true">
                        <img v-for="(photo, index) in ctaPhotos" :key="index" class="cta-collage__image"
                            :src="photo.src" :alt="photo.alt" loading="lazy" />
                    </div>
                </div>
            </div>
        </section>
    </main>
</template>

<script>
const ICONS = {
    instagram: `
        <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M7.5 2.75h9A4.75 4.75 0 0 1 21.25 7.5v9A4.75 4.75 0 0 1 16.5 21.25h-9A4.75 4.75 0 0 1 2.75 16.5v-9A4.75 4.75 0 0 1 7.5 2.75Z"
                stroke="currentColor"
                stroke-width="1.7"
            />

            <path
                d="M12 16.25A4.25 4.25 0 1 0 12 7.75a4.25 4.25 0 0 0 0 8.5Z"
                stroke="currentColor"
                stroke-width="1.7"
            />

            <path
                d="M17.2 6.9h.01"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
            />
        </svg>
    `,

    facebook: `
        <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M13.5 21v-7h2.4l.4-3h-2.8V9.1c0-.9.3-1.6 1.7-1.6H16.4V4.8c-.6-.1-1.6-.2-2.7-.2-2.6 0-4.2 1.6-4.2 4.5V11H7.2v3h2.3v7h4Z"
            />
        </svg>
    `,

    linkedin: `
        <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M6.5 8.25H3.25V21H6.5V8.25ZM4.88 3A1.88 1.88 0 1 0 4.88 6.75 1.88 1.88 0 0 0 4.88 3ZM21 13.7C21 9.85 18.94 8.06 16.2 8.06c-2.21 0-3.2 1.22-3.76 2.07V8.25H9.2V21h3.24v-6.32c0-1.67.32-3.29 2.39-3.29 2.04 0 2.06 1.91 2.06 3.4V21H21v-7.3Z"
            />
        </svg>
    `,

    tiktok: `
        <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M14.5 3h3.1c.25 1.45 1.06 2.67 2.4 3.45V9.5a8.14 8.14 0 0 1-2.72-.72v6.08A6.14 6.14 0 1 1 12 8.78v3.12a3.06 3.06 0 1 0 2.5 3V3Z"
            />
        </svg>
    `,
};

export default {
    name: "OurSocialsPage",

    data() {
        return {
            socialsList: [
                {
                    name: "Instagram",
                    icon: ICONS.instagram,
                    label: "Stories",
                    handle: "@bridgeofhope.mc",
                    url: "https://www.instagram.com/bridgeofhope.mc/",
                    description:
                        "Visual stories, community moments, impact highlights, and updates from M.C Bridge of Hope.",
                    image:
                        "https://images.pexels.com/photos/6994985/pexels-photo-6994985.jpeg?auto=compress&cs=tinysrgb&w=1200&h=900&dpr=1",
                    imageAlt:
                        "People smiling and connecting in the community",
                },

                {
                    name: "Facebook",
                    icon: ICONS.facebook,
                    label: "Community",
                    handle: "M.C Bridge of Hope",
                    url:
                        "https://www.facebook.com/profile.php?id=61594445814032",
                    description:
                        "Community updates, outreach news, events, announcements, and opportunities to get involved.",
                    image:
                        "https://images.pexels.com/photos/6646869/pexels-photo-6646869.jpeg?auto=compress&cs=tinysrgb&w=1200&h=900&dpr=1",
                    imageAlt:
                        "Community gathering and smiling faces",
                },

                {
                    name: "LinkedIn",
                    icon: ICONS.linkedin,
                    label: "Partners",
                    handle: "M.C Bridge of Hope",
                    url:
                        "https://www.linkedin.com/in/mc-bridge-of-hope-44aa23434/",
                    description:
                        "Professional updates, partnerships, organisation news, and opportunities to collaborate.",
                    image:
                        "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=1200&h=900&dpr=1",
                    imageAlt:
                        "Professional community discussion and collaboration",
                },

                {
                    name: "TikTok",
                    icon: ICONS.tiktok,
                    label: "Reach",
                    handle: "@marychan.org",
                    url:
                        "https://www.tiktok.com/@marychan.org?_r=1&_t=ZS-99dXxN98DoL",
                    description:
                        "Short videos, community moments, outreach highlights, and messages of encouragement.",
                    image:
                        "https://images.pexels.com/photos/6646919/pexels-photo-6646919.jpeg?auto=compress&cs=tinysrgb&w=1200&h=900&dpr=1",
                    imageAlt:
                        "Volunteers organising community support items",
                },
            ],

            galleryPhotos: [
                {
                    src:
                        "https://images.pexels.com/photos/6647037/pexels-photo-6647037.jpeg?auto=compress&cs=tinysrgb&w=1200&h=900&dpr=1",
                    alt:
                        "Community support in action",
                },

                {
                    src:
                        "https://images.pexels.com/photos/6647019/pexels-photo-6647019.jpeg?auto=compress&cs=tinysrgb&w=1200&h=900&dpr=1",
                    alt:
                        "Helping hands together",
                },

                {
                    src:
                        "https://images.pexels.com/photos/3184405/pexels-photo-3184405.jpeg?auto=compress&cs=tinysrgb&w=1200&h=900&dpr=1",
                    alt:
                        "Mentorship and community discussion",
                },

                {
                    src:
                        "https://images.pexels.com/photos/6348129/pexels-photo-6348129.jpeg?auto=compress&cs=tinysrgb&w=1200&h=900&dpr=1",
                    alt:
                        "Care packs and community support materials",
                },
            ],

            ctaPhotos: [
                {
                    src:
                        "https://images.pexels.com/photos/6994980/pexels-photo-6994980.jpeg?auto=compress&cs=tinysrgb&w=900&h=900&dpr=1",
                    alt:
                        "Community outreach",
                },

                {
                    src:
                        "https://images.pexels.com/photos/32941093/pexels-photo-32941093.jpeg?auto=compress&cs=tinysrgb&w=900&h=900&dpr=1",
                    alt:
                        "Helping hands",
                },

                {
                    src:
                        "https://images.pexels.com/photos/3184431/pexels-photo-3184431.jpeg?auto=compress&cs=tinysrgb&w=900&h=900&dpr=1",
                    alt:
                        "Supportive conversation",
                },

                {
                    src:
                        "https://images.pexels.com/photos/6646869/pexels-photo-6646869.jpeg?auto=compress&cs=tinysrgb&w=900&h=900&dpr=1",
                    alt:
                        "Community group moment",
                },
            ],
        };
    },

    computed: {
        featuredSocial() {
            return this.socialsList[0];
        },
    },

    mounted() {
        if (typeof window !== "undefined") {
            window.scrollTo(0, 0);
        }
    },
};
</script>

<style scoped lang="scss">
.socials-page {
    --bg: #f7f3ec;
    --cream: #fffaf1;
    --paper: #ffffff;
    --ink: #15202b;
    --muted: rgba(21, 32, 43, 0.68);
    --soft: rgba(21, 32, 43, 0.1);
    --brand: #0b3f57;
    --brand-deep: #072f42;
    --accent: #b9996b;
    --accent-soft: rgba(185, 153, 107, 0.22);
    --radius-xl: 34px;
    --radius-lg: 24px;
    --radius-md: 16px;
    --shadow: 0 24px 70px rgba(7, 47, 66, 0.16);
    --shadow-soft: 0 14px 36px rgba(7, 47, 66, 0.1);

    min-height: 100%;
    overflow: hidden;
    color: var(--ink);

    background:
        radial-gradient(circle at top left,
            rgba(185, 153, 107, 0.18),
            transparent 34%),
        linear-gradient(180deg,
            var(--bg) 0%,
            #f9fbfc 100%);

    .socials-page__shell {
        width: min(1160px, calc(100% - 32px));
        margin: 0 auto;

        @media (min-width: 760px) {
            width: min(1160px, calc(100% - 48px));
        }

        @media (min-width: 1120px) {
            width: min(1180px, calc(100% - 72px));
        }
    }

    .socials-eyebrow {
        margin: 0 0 10px;
        color: var(--accent);
        font-size: 12px;
        font-weight: 950;
        letter-spacing: 0.16em;
        text-transform: uppercase;
    }

    .socials-btn {
        appearance: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 48px;
        padding: 13px 20px;
        border: 0;
        border-radius: 999px;
        cursor: pointer;
        font-family: inherit;
        font-weight: 950;
        text-decoration: none;

        transition:
            transform 180ms ease,
            box-shadow 180ms ease,
            background 180ms ease,
            opacity 180ms ease;

        &:hover {
            transform: translateY(-2px);
        }
    }

    .socials-btn--primary {
        color: #fff;

        background:
            radial-gradient(circle at top left,
                rgba(255, 255, 255, 0.24),
                transparent 32%),
            linear-gradient(135deg,
                var(--brand),
                var(--brand-deep));

        box-shadow:
            0 16px 34px rgba(7, 47, 66, 0.25);
    }

    .socials-btn--light {
        color: var(--brand);
        background: rgba(255, 255, 255, 0.86);
        box-shadow: 0 12px 28px rgba(7, 47, 66, 0.1);
    }

    .socials-hero {
        position: relative;
        isolation: isolate;
        padding: 82px 0 72px;

        background:
            linear-gradient(135deg,
                rgba(11, 63, 87, 0.98),
                rgba(7, 47, 66, 0.94)),
            radial-gradient(circle at 10% 10%,
                rgba(185, 153, 107, 0.34),
                transparent 32%);

        @media (max-width: 720px) {
            padding: 58px 0 46px;
        }
    }

    .socials-hero__glow {
        position: absolute;
        z-index: -1;
        border-radius: 999px;
        filter: blur(2px);
        opacity: 0.74;
        pointer-events: none;
    }

    .socials-hero__glow--one {
        top: -90px;
        right: 10%;
        width: 260px;
        height: 260px;
        background: rgba(185, 153, 107, 0.26);
    }

    .socials-hero__glow--two {
        bottom: -130px;
        left: 6%;
        width: 320px;
        height: 320px;
        background: rgba(255, 250, 241, 0.12);
    }

    .socials-hero__shell {
        display: grid;
        gap: 28px;
        align-items: center;

        @media (min-width: 940px) {
            grid-template-columns: minmax(0, 1fr) 410px;
        }
    }

    .socials-hero__content {
        max-width: 780px;
    }

    .socials-hero__title {
        margin: 0;
        max-width: 850px;
        color: #fff;

        font-size:
            clamp(44px,
                7vw,
                86px);

        line-height: 0.95;
        font-weight: 1000;
        letter-spacing: -2px;
    }

    .socials-hero__text {
        margin: 22px 0 0;
        max-width: 680px;
        color: rgba(255, 255, 255, 0.78);
        font-size: 17px;
        line-height: 1.85;
    }

    .socials-hero__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 28px;
    }

    .feature-card {
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.22);
        border-radius: var(--radius-xl);
        background: rgba(255, 255, 255, 0.12);
        box-shadow: var(--shadow);
        backdrop-filter: blur(18px);
    }

    .feature-card__imageWrap {
        position: relative;
        height: 300px;
        overflow: hidden;
    }

    .feature-card__image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    .feature-card__badge {
        position: absolute;
        top: 16px;
        left: 16px;
        min-height: 34px;
        padding: 8px 12px;
        border-radius: 999px;
        color: var(--brand);
        background: rgba(255, 255, 255, 0.9);
        font-size: 11px;
        font-weight: 950;
        letter-spacing: 0.12em;
        text-transform: uppercase;
    }

    .feature-card__body {
        display: flex;
        gap: 14px;
        align-items: center;
        padding: 18px;

        h2 {
            margin: 0;
            color: #fff;
            font-size: 24px;
            font-weight: 1000;
        }

        p {
            margin: 4px 0 0;
            color: rgba(255, 255, 255, 0.7);
            font-weight: 800;
        }
    }

    .feature-card__icon {
        display: grid;
        width: 56px;
        height: 56px;
        place-items: center;
        flex: 0 0 auto;
        border-radius: 20px;
        color: var(--brand);
        background: #fff;
    }

    .feature-card__icon :deep(svg) {
        display: block;
    }

    .feature-card__small {
        margin: 0 0 4px !important;
        color: var(--accent) !important;
        font-size: 11px;
        font-weight: 950;
        letter-spacing: 0.12em;
        text-transform: uppercase;
    }

    .socials-section {
        padding: 72px 0;

        background:
            radial-gradient(circle at top right,
                rgba(185, 153, 107, 0.14),
                transparent 34%),
            linear-gradient(180deg,
                #f9fbfc 0%,
                var(--cream) 100%);

        @media (max-width: 720px) {
            padding: 50px 0;
        }
    }

    .socials-section__head,
    .gallery-section__head {
        max-width: 760px;
        margin-bottom: 26px;

        h2 {
            margin: 0;
            color: var(--brand);
            font-size: clamp(32px, 4vw, 52px);
            line-height: 1;
            font-weight: 1000;
            letter-spacing: -1px;
        }

        p {
            margin: 14px 0 0;
            color: var(--muted);
            line-height: 1.75;
        }
    }

    .socials-grid {
        display: grid;
        gap: 18px;

        @media (min-width: 700px) {
            grid-template-columns: repeat(2, 1fr);
        }

        @media (min-width: 1080px) {
            grid-template-columns: repeat(4, 1fr);
        }
    }

    .social-card {
        overflow: hidden;
        border: 1px solid rgba(7, 47, 66, 0.1);
        border-radius: var(--radius-xl);
        color: var(--ink);
        background: rgba(255, 255, 255, 0.92);
        box-shadow: var(--shadow-soft);
        text-decoration: none;

        transition:
            transform 190ms ease,
            box-shadow 190ms ease,
            border-color 190ms ease;

        &:hover {
            transform: translateY(-6px);
            border-color: rgba(185, 153, 107, 0.38);
            box-shadow: 0 24px 54px rgba(7, 47, 66, 0.14);

            .social-card__image {
                transform: scale(1.05);
            }
        }
    }

    .social-card__media {
        position: relative;
        height: 240px;
        overflow: hidden;
    }

    .social-card__image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        transition: transform 420ms ease;
    }

    .social-card__overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding: 14px;

        background:
            linear-gradient(180deg,
                rgba(7, 47, 66, 0.42),
                transparent 48%),
            linear-gradient(0deg,
                rgba(7, 47, 66, 0.16),
                transparent 45%);

        span,
        strong {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.9);
            color: var(--brand);
            font-weight: 1000;
        }

        span {
            min-height: 34px;
            padding: 8px 11px;
            border-radius: 999px;
            font-size: 11px;
            letter-spacing: 0.1em;
            text-transform: uppercase;
        }

        strong {
            width: 44px;
            height: 44px;
            border-radius: 16px;
        }
    }

    .social-card__platform-icon :deep(svg) {
        display: block;
    }

    .social-card__body {
        display: grid;
        gap: 12px;
        padding: 18px;

        h3 {
            margin: 0;
            color: var(--brand);
            font-size: 24px;
            font-weight: 1000;
            line-height: 1.1;
        }
    }

    .social-card__handle {
        margin: 5px 0 0;
        color: rgba(21, 32, 43, 0.58);
        font-weight: 850;
    }

    .social-card__desc {
        margin: 0;
        color: var(--muted);
        line-height: 1.7;
    }

    .social-card__link {
        color: var(--accent);
        font-size: 13px;
        font-weight: 1000;
        letter-spacing: 0.08em;
        text-transform: uppercase;
    }

    .gallery-section {
        padding: 72px 0;
        background: #fff;

        @media (max-width: 720px) {
            padding: 50px 0;
        }
    }

    .gallery-section__head {
        text-align: center;
        margin-left: auto;
        margin-right: auto;

        p {
            margin-left: auto;
            margin-right: auto;
        }
    }

    .gallery-grid {
        display: grid;
        gap: 16px;

        @media (min-width: 900px) {
            grid-template-columns: 1.2fr 0.8fr 1fr;
            grid-template-rows: 230px 230px;
        }
    }

    .gallery-card {
        position: relative;
        overflow: hidden;
        min-height: 280px;
        border-radius: var(--radius-xl);
        box-shadow: var(--shadow-soft);

        @media (min-width: 900px) {
            min-height: auto;
        }
    }

    .gallery-card--1 {
        @media (min-width: 900px) {
            grid-row: span 2;
        }
    }

    .gallery-card--4 {
        @media (min-width: 900px) {
            grid-column: span 2;
        }
    }

    .gallery-card__image {
        width: 100%;
        height: 100%;
        min-height: inherit;
        object-fit: cover;
        display: block;
    }

    .gallery-card__shade {
        position: absolute;
        inset: 0;

        background:
            linear-gradient(180deg,
                transparent,
                rgba(7, 47, 66, 0.18)),
            radial-gradient(circle at top right,
                rgba(185, 153, 107, 0.1),
                transparent 45%);

        pointer-events: none;
    }

    .socials-cta {
        padding: 72px 0;

        background:
            radial-gradient(circle at top left,
                rgba(185, 153, 107, 0.16),
                transparent 34%),
            linear-gradient(180deg,
                #fff 0%,
                var(--cream) 100%);

        @media (max-width: 720px) {
            padding: 50px 0;
        }
    }

    .socials-cta__panel {
        overflow: hidden;
        display: grid;
        gap: 0;
        align-items: center;
        border: 1px solid rgba(7, 47, 66, 0.1);
        border-radius: var(--radius-xl);

        background:
            radial-gradient(circle at top right,
                rgba(185, 153, 107, 0.15),
                transparent 38%),
            rgba(255, 255, 255, 0.92);

        box-shadow: var(--shadow);

        @media (min-width: 920px) {
            grid-template-columns:
                minmax(0, 1fr) 470px;
        }
    }

    .socials-cta__content {
        padding: 30px;

        h2 {
            margin: 0;
            max-width: 720px;
            color: var(--brand);
            font-size: clamp(32px, 4.6vw, 58px);
            line-height: 0.98;
            font-weight: 1000;
            letter-spacing: -1.2px;
        }

        p {
            margin: 16px 0 0;
            max-width: 640px;
            color: var(--muted);
            line-height: 1.8;
        }

        @media (max-width: 640px) {
            padding: 22px;
        }
    }

    .socials-cta__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 24px;
    }

    .cta-collage {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        padding: 22px;

        @media (max-width: 640px) {
            padding: 18px;
        }
    }

    .cta-collage__image {
        width: 100%;
        height: 180px;
        object-fit: cover;
        display: block;
        border-radius: 22px;
        box-shadow: 0 12px 28px rgba(7, 47, 66, 0.1);

        &:nth-child(1),
        &:nth-child(4) {
            transform: translateY(18px);
        }

        @media (max-width: 640px) {
            height: 140px;

            &:nth-child(1),
            &:nth-child(4) {
                transform: none;
            }
        }
    }

    @media (max-width: 560px) {

        .socials-hero__actions,
        .socials-cta__actions,
        .socials-btn {
            width: 100%;
        }

        .feature-card__imageWrap {
            height: 240px;
        }

        .social-card__media {
            height: 230px;
        }
    }

    @media (prefers-reduced-motion: reduce) {

        *,
        *::before,
        *::after {
            scroll-behavior: auto !important;
            animation-duration: 0.001ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.001ms !important;
        }
    }
}
</style>